import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { EVENT_TYPES, CATEGORIES } from "@/lib/campaignMeta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarDays, Plus, Pencil, Trash2, MapPin, Loader2, Radar, EyeOff } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", event_type: "Boat Show", start_date: "", end_date: "", location: "", notes: "", inventory_fit_categories: [], event_learning_boat_categories: [], event_audience: "unknown" };
const PARTICIPATION = [["unknown", "Unknown"], ["attending", "Attending"], ["exhibiting", "Exhibiting"], ["sponsoring", "Sponsoring"], ["not_attending", "Not Attending"]];
const AUDIENCE = [["consumer", "Consumer (boat shoppers)"], ["industry_trade", "Industry / Trade"], ["mixed", "Mixed"], ["unknown", "Unknown / not sure"]];
const MARINE_EVENT_TYPES = ["Boat Show", "Marine Expo", "Fishing Expo", "Boating Expo", "Yacht / Marine Show", "Fishing Tournament"];
const isMarineEvent = (t) => MARINE_EVENT_TYPES.includes(t);
const TYPE_LABEL = (t) => String(t || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

function fmt(d) {
  if (!d) return "";
  try { return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return d; }
}
function daysAway(d) {
  if (!d) return null;
  const t = new Date(d + "T00:00:00");
  const now = new Date(); now.setHours(0, 0, 0, 0);
  return Math.round((t - now) / 86400000);
}
function awayLabel(d) {
  const n = daysAway(d);
  if (n === null) return "";
  if (n === 0) return "Today";
  if (n > 0) return `${n} day${n === 1 ? "" : "s"} away`;
  return `${Math.abs(n)} day${n === -1 ? "" : "s"} ago`;
}

export default function Calendar() {
  const [events, setEvents] = useState([]);
  const [discovered, setDiscovered] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => Promise.all([
    api.get("/events").then(({ data }) => setEvents(data)).catch(() => {}),
    api.get("/local-events/calendar").then(({ data }) => setDiscovered(data)).catch(() => setDiscovered([])),
  ]).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleCat = (field, cat) => setForm((f) => {
    const arr = f[field] || [];
    return { ...f, [field]: arr.includes(cat) ? arr.filter((c) => c !== cat) : [...arr, cat] };
  });
  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (ev) => { setEditing(ev); setForm({ ...EMPTY, ...ev }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.start_date) return toast.error("Event name and start date are required");
    setSaving(true);
    try {
      if (editing) await api.put(`/events/${editing.id}`, form);
      else await api.post("/events", form);
      toast.success(editing ? "Event updated" : "Event added to calendar");
      setOpen(false); load();
    } catch { toast.error("Could not save event"); }
    finally { setSaving(false); }
  };

  const del = async (ev) => {
    if (!window.confirm(`Delete "${ev.name}"?`)) return;
    try { await api.delete(`/events/${ev.id}`); toast.success("Event deleted"); load(); }
    catch { toast.error("Could not delete event"); }
  };

  const setParticipation = async (ev, status) => {
    setDiscovered((list) => list.map((e) => (e.event_id === ev.event_id ? { ...e, dealer_participation_status: status } : e)));
    try { await api.post(`/local-events/${ev.event_id}/participation`, { status }); }
    catch { toast.error("Could not update participation"); load(); }
  };
  const dismiss = async (ev) => {
    try { await api.post(`/local-events/${ev.event_id}/dismiss`); toast.success("Event hidden"); load(); }
    catch { toast.error("Could not hide event"); }
  };

  const showDealer = filter === "all" || filter === "dealer";
  const showRecjet = filter === "all" || filter === "recjet";
  const nothing = !loading && events.length === 0 && discovered.length === 0;

  return (
    <div className="space-y-6 fade-up" data-testid="calendar-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-bold text-navy">Marketing Calendar</h1>
          <p className="text-muted-foreground mt-1">Plan boat shows, open houses and promotions. RecJet Mind also surfaces nearby marine events it discovers.</p>
        </div>
        <Button onClick={openNew} className="bg-navy hover:bg-navy-light text-white h-11" data-testid="add-event-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add Event
        </Button>
      </div>

      <div className="flex gap-2" data-testid="calendar-filter">
        {[["all", "All"], ["dealer", "Dealer Events"], ["recjet", "RecJet Mind Events"]].map(([v, lbl]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`filter-${v}`}
            className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${filter === v ? "bg-navy text-white border-navy" : "bg-white text-navy border-slate-300 hover:border-navy"}`}>
            {lbl}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-20 text-muted-foreground">Loading calendar...</div>
      ) : nothing ? (
        <div className="text-center py-20 text-muted-foreground bg-white rounded-2xl border border-border" data-testid="calendar-empty">
          <CalendarDays className="w-10 h-10 mx-auto mb-3 opacity-40" />
          No events yet. Add your first boat show, open house or promotion to start planning.
        </div>
      ) : (
        <div className="space-y-3" data-testid="events-list">
          {showDealer && events.map((ev) => {
            const n = daysAway(ev.end_date || ev.start_date);
            const upcoming = n === null ? false : n >= 0;
            return (
              <div key={ev.id} className="bg-white rounded-2xl border border-border p-5 flex items-start gap-4 card-hover" data-testid={`event-${ev.id}`}>
                <div className="w-12 h-12 rounded-xl gold-gradient flex items-center justify-center shrink-0">
                  <CalendarDays className="w-5 h-5 text-navy" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-serif text-lg font-bold text-navy">{ev.name}</h3>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gold bg-gold/10 px-2.5 py-1 rounded-full">{ev.event_type}</span>
                    {ev.start_date && <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${upcoming ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{awayLabel(ev.end_date || ev.start_date)}</span>}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {fmt(ev.start_date)}{ev.end_date && ev.end_date !== ev.start_date ? ` to ${fmt(ev.end_date)}` : ""}
                    {ev.location ? <span className="inline-flex items-center gap-1 ml-2"><MapPin className="w-3.5 h-3.5" /> {ev.location}</span> : null}
                  </div>
                  {ev.notes && <p className="text-sm text-navy/70 mt-2">{ev.notes}</p>}
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button variant="outline" size="icon" onClick={() => openEdit(ev)} data-testid={`edit-event-${ev.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="outline" size="icon" onClick={() => del(ev)} className="text-red-600 hover:text-red-700" data-testid={`delete-event-${ev.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            );
          })}

          {showRecjet && discovered.map((ev) => {
            const upcoming = (daysAway(ev.end_date || ev.start_date) ?? -1) >= 0;
            return (
              <div key={ev.event_id} className="bg-white rounded-2xl border border-navy/20 p-5 flex items-start gap-4 card-hover" data-testid={`discovered-${ev.event_id}`}>
                <div className="w-12 h-12 rounded-xl bg-navy flex items-center justify-center shrink-0">
                  <Radar className="w-5 h-5 text-gold" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-serif text-lg font-bold text-navy">{ev.name}</h3>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-navy bg-navy/10 px-2.5 py-1 rounded-full">Discovered by RecJet Mind</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gold bg-gold/10 px-2.5 py-1 rounded-full">{TYPE_LABEL(ev.event_type)}</span>
                    {ev.start_date && <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${upcoming ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{awayLabel(ev.end_date || ev.start_date)}</span>}
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600 bg-slate-100 px-2.5 py-1 rounded-full">{ev.verification_status}</span>
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {fmt(ev.start_date)}{ev.end_date && ev.end_date !== ev.start_date ? ` to ${fmt(ev.end_date)}` : ""}
                    {(ev.venue_name || ev.city) ? <span className="inline-flex items-center gap-1 ml-2"><MapPin className="w-3.5 h-3.5" /> {[ev.venue_name, ev.city, ev.state].filter(Boolean).join(", ")}</span> : null}
                    {ev.distance_miles != null ? <span className="ml-2">about {Math.round(ev.distance_miles)} mi away</span> : null}
                  </div>
                  {ev.relevance_reason && <p className="text-sm text-navy/70 mt-2">{ev.relevance_reason}</p>}
                  {ev.source_url && <a href={ev.source_url} target="_blank" rel="noreferrer" className="text-xs text-navy underline mt-1 inline-block" data-testid={`source-${ev.event_id}`}>View source ({ev.source_name})</a>}
                  <div className="mt-3 flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Your participation</span>
                    <Select value={ev.dealer_participation_status || "unknown"} onValueChange={(v) => setParticipation(ev, v)}>
                      <SelectTrigger className="h-8 w-40" data-testid={`participation-${ev.event_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">{PARTICIPATION.map(([v, lbl]) => <SelectItem key={v} value={v}>{lbl}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button variant="outline" size="icon" onClick={() => dismiss(ev)} className="text-slate-500 hover:text-slate-700" title="Hide this event" data-testid={`dismiss-${ev.event_id}`}><EyeOff className="w-4 h-4" /></Button>
                </div>
              </div>
            );
          })}

          {showRecjet && !loading && discovered.length === 0 && (
            <div className="text-sm text-muted-foreground bg-white rounded-2xl border border-border p-5" data-testid="discovered-empty">
              No upcoming marine events found within 60 miles.
            </div>
          )}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg" data-testid="event-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-navy">{editing ? "Edit Event" : "Add Event"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5"><Label>Event Name</Label><Input required value={form.name} onChange={set("name")} placeholder="e.g. Atlanta Boat Show" data-testid="event-name" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Event Type</Label>
                <Select value={form.event_type} onValueChange={(v) => setForm((f) => ({ ...f, event_type: v }))}>
                  <SelectTrigger data-testid="event-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{EVENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Location</Label><Input value={form.location} onChange={set("location")} placeholder="City or venue" data-testid="event-location" /></div>
              <div className="space-y-1.5"><Label>Start Date</Label><Input type="date" value={form.start_date} onChange={set("start_date")} data-testid="event-start" /></div>
              <div className="space-y-1.5"><Label>End Date</Label><Input type="date" value={form.end_date} onChange={set("end_date")} data-testid="event-end" /></div>
            </div>
            <div className="space-y-1.5"><Label>Description / Notes</Label><Textarea value={form.notes} onChange={set("notes")} rows={3} data-testid="event-notes" /></div>
            {isMarineEvent(form.event_type) && (
              <div className="space-y-4 rounded-xl border border-navy/15 bg-navy/[0.03] p-4" data-testid="event-intelligence-section">
                <div className="flex items-center gap-2">
                  <Radar className="w-4 h-4 text-navy" />
                  <span className="text-sm font-bold text-navy">RecJet Mind Event Intelligence</span>
                </div>
                <p className="text-xs text-muted-foreground -mt-2">This qualifying marine event feeds the same intelligence as events RecJet Mind discovers.</p>
                <div className="space-y-1.5">
                  <Label className="text-sm">Event Audience</Label>
                  <p className="text-xs text-muted-foreground">Consumer boat-shopping event, or a marine industry / trade event? Trade-only events stay on your calendar but do not drive consumer boat-buying signals.</p>
                  <Select value={form.event_audience || "unknown"} onValueChange={(v) => setForm((f) => ({ ...f, event_audience: v }))}>
                    <SelectTrigger className="h-9" data-testid="event-audience"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{AUDIENCE.map(([v, lbl]) => <SelectItem key={v} value={v}>{lbl}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Event Inventory Fit</Label>
                  <p className="text-xs text-muted-foreground">Which boat categories do you want to promote around this event?</p>
                  <div className="flex flex-wrap gap-2" data-testid="inventory-fit-chips">
                    {CATEGORIES.map((cat) => {
                      const on = (form.inventory_fit_categories || []).includes(cat);
                      return (
                        <button type="button" key={cat} onClick={() => toggleCat("inventory_fit_categories", cat)}
                          data-testid={`inventory-fit-${cat.replace(/\s+/g, "-").toLowerCase()}`}
                          className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${on ? "bg-navy text-white border-navy" : "bg-white text-navy border-slate-300 hover:border-navy"}`}>
                          {cat}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Event Learning</Label>
                  <p className="text-xs text-muted-foreground">Historically, what types of boats generate the most interest or sell best around this show?</p>
                  <div className="flex flex-wrap gap-2" data-testid="event-learning-chips">
                    {CATEGORIES.map((cat) => {
                      const on = (form.event_learning_boat_categories || []).includes(cat);
                      return (
                        <button type="button" key={cat} onClick={() => toggleCat("event_learning_boat_categories", cat)}
                          data-testid={`event-learning-${cat.replace(/\s+/g, "-").toLowerCase()}`}
                          className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${on ? "bg-gold text-navy border-gold" : "bg-white text-navy border-slate-300 hover:border-navy"}`}>
                          {cat}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-navy hover:bg-navy-light text-white" data-testid="save-event-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Save Changes" : "Add Event"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
